import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useApi, discoveryApiRef } from '@backstage/core-plugin-api';
import { MarketplaceData, MarketplaceItem, ItemType } from './types';
import { useMarketplaceFetchApi } from './marketplaceFetch';

interface MarketplaceErrorResponse {
  error?: string;
  hint?: string;
}

function isMarketplaceErrorResponse(value: unknown): value is MarketplaceErrorResponse {
  return !!value && typeof value === 'object' && ('error' in value || 'hint' in value);
}

function isMarketplaceData(value: unknown): value is MarketplaceData {
  return !!value && typeof value === 'object' && Array.isArray((value as MarketplaceData).items);
}

interface MarketplaceContextValue {
  items: MarketplaceItem[];
  loading: boolean;
  error: string | null;
  cachedAt: string | null;
  agents: MarketplaceItem[];
  skills: MarketplaceItem[];
  prompts: MarketplaceItem[];
  refresh: () => void;
}

const MarketplaceContext = createContext<MarketplaceContextValue>({
  items: [], loading: false, error: null, cachedAt: null,
  agents: [], skills: [], prompts: [], refresh: () => {},
});

export function MarketplaceProvider({ children }: { children: ReactNode }) {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useMarketplaceFetchApi();
  const [data, setData] = useState<MarketplaceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('marketplace');
        const resp = await fetchApi.fetch(`${baseUrl}/items`);
        const json = await resp.json().catch(() => null) as unknown;
        if (!resp.ok) {
          const message = isMarketplaceErrorResponse(json) && typeof json.hint === 'string'
            ? json.hint
            : isMarketplaceErrorResponse(json) && typeof json.error === 'string'
              ? json.error
              : `HTTP ${resp.status}`;
          throw new Error(message);
        }
        if (!isMarketplaceData(json)) {
          throw new Error('Marketplace returned an invalid response');
        }
        if (!cancelled) setData(json);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Failed to load marketplace');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi, tick]);

  const items = data?.items ?? [];
  const byType = (t: ItemType) => items.filter(i => i.type === t);

  return (
    <MarketplaceContext.Provider value={{
      items, loading, error,
      cachedAt: data?.cachedAt ?? null,
      agents: byType('agent'),
      skills: byType('skill'),
      prompts: byType('prompt'),
      refresh: () => setTick(n => n + 1),
    }}>
      {children}
    </MarketplaceContext.Provider>
  );
}

export function useMarketplace() {
  return useContext(MarketplaceContext);
}
